import { useLayoutEffect, useState, type ReactElement } from 'react';
import { Box, Button, Paper, Popper, Portal, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  GUIDE_NOTICE,
  guideParamsFromContext,
  guideProgress,
  translateGuideKey,
  useGuideStore,
  type GuideTranslator,
} from '@/lib/guides/engine';

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function readRect(element: HTMLElement): HighlightRect {
  const rect = element.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

function GuideHighlight({ element }: { element: HTMLElement }): ReactElement | null {
  const theme = useTheme();
  const [rect, setRect] = useState<HighlightRect | null>(null);

  useLayoutEffect(() => {
    const update = (): void => {
      if (!element.isConnected) return;
      setRect(readRect(element));
    };
    update();
    const observed = new ResizeObserver(update);
    observed.observe(element);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      observed.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [element]);

  if (!rect) return null;

  return (
    <Portal>
      <Box
        aria-hidden
        data-testid="guide-highlight"
        sx={{
          position: 'fixed',
          top: rect.top - 4,
          left: rect.left - 4,
          width: Math.max(rect.width + 8, 0),
          height: Math.max(rect.height + 8, 0),
          borderRadius: 1,
          pointerEvents: 'none',
          zIndex: theme.zIndex.modal + 1,
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 0,
          boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.28)}`,
        }}
      />
    </Portal>
  );
}

function asGuideTranslator(translate: unknown): GuideTranslator {
  const call = translate as (key: string, options?: object) => unknown;
  return (key, options) => call(key, options);
}

/**
 * Spotlight for the active guide step. Sits above MUI dialogs so the wizard
 * and the instruction are visible together. Renders nothing until the anchor exists.
 */
export function GuideOverlay(): ReactElement | null {
  const theme = useTheme();
  const { t } = useTranslation('assistant');
  const { t: guideT } = useTranslation('guides', { useSuspense: false });
  const active = useGuideStore((state) => state.active);
  const anchorEl = useGuideStore((state) => state.anchorEl);
  const anchorStatus = useGuideStore((state) => state.anchorStatus);
  const goBack = useGuideStore((state) => state.back);
  const goNext = useGuideStore((state) => state.next);
  const skip = useGuideStore((state) => state.skip);
  const cancel = useGuideStore((state) => state.cancel);

  if (!active || anchorStatus !== 'found' || !anchorEl || !anchorEl.isConnected) return null;

  const step = active.guide.steps.find((item) => item.id === active.stepId);
  if (!step) return null;

  const { current, total } = guideProgress(active);
  const assistant = asGuideTranslator(t);
  const guides = asGuideTranslator(guideT);
  const params = guideParamsFromContext(active.ctx);
  const stepText =
    translateGuideKey(guides, step.textKey, params) ??
    translateGuideKey(assistant, GUIDE_NOTICE.missingText) ??
    '';
  const regionLabel =
    translateGuideKey(assistant, 'guide.region') ??
    translateGuideKey(assistant, GUIDE_NOTICE.missingText) ??
    '';
  const aboveModal = theme.zIndex.modal + 2;

  return (
    <>
      <GuideHighlight element={anchorEl} />
      <Popper
        open
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 8 } },
        ]}
        sx={{ zIndex: aboveModal }}
        style={{ zIndex: aboveModal }}
      >
        <Paper
          elevation={8}
          role="region"
          aria-label={regionLabel}
          data-testid="guide-overlay"
          sx={{ p: 1.5, maxWidth: 320 }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {stepText}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
            {t('guide.progress', { current, total })}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Button size="small" onClick={goBack} disabled={active.history.length === 0}>
              {t('guide.back')}
            </Button>
            {step.doneWhen === undefined && (
              <Button size="small" variant="contained" onClick={goNext}>
                {t('guide.next')}
              </Button>
            )}
            <Button size="small" onClick={skip}>
              {t('guide.skip')}
            </Button>
            <Button size="small" color="inherit" onClick={cancel}>
              {t('guide.stop')}
            </Button>
          </Box>
        </Paper>
      </Popper>
    </>
  );
}
